import type { TFunction } from 'i18next';
import { humanizeGateStatus } from '../utils/format';
import type {
  CommunicationChannel,
  CommunicationOutcome,
  FileKind,
  FileReferenceRole,
  FileSource,
  SendOperationStatus,
} from '../api/types';

/**
 * The translation keys behind the status chips, kept out of the component file so both the chips and
 * the places that need a bare string — grid cells, tooltips, confirmation prose — resolve them from
 * one list. Resolve with `t(...)`; the values live in the locale files.
 */
export const operationStatusLabelKeys = {
  Queued: 'status.operation.Queued',
  Submitted: 'status.operation.Submitted',
  Duplicate: 'status.operation.Duplicate',
  Rejected: 'status.operation.Rejected',
  Failed: 'status.operation.Failed',
} as const satisfies Record<SendOperationStatus, string>;

export const fileKindLabelKeys = {
  BisDocument: 'fileKinds.BisDocument',
  IsdocDocument: 'fileKinds.IsdocDocument',
  Attachment: 'fileKinds.Attachment',
  InboundXml: 'fileKinds.InboundXml',
  InboundPdf: 'fileKinds.InboundPdf',
  InboundJson: 'fileKinds.InboundJson',
  InboundAttachment: 'fileKinds.InboundAttachment',
} as const satisfies Record<FileKind, string>;

export const fileSourceLabelKeys = {
  Upload: 'fileSources.Upload',
  Generated: 'fileSources.Generated',
  FlowisInbound: 'fileSources.FlowisInbound',
} as const satisfies Record<FileSource, string>;

export const fileRoleLabelKeys = {
  Document: 'fileRoles.Document',
  SentPayload: 'fileRoles.SentPayload',
  Attachment: 'fileRoles.Attachment',
} as const satisfies Record<FileReferenceRole, string>;

export const communicationChannelLabelKeys = {
  SubmitQueue: 'communication.channels.SubmitQueue',
  StatusQueue: 'communication.channels.StatusQueue',
} as const satisfies Record<CommunicationChannel, string>;

export const communicationOutcomeLabelKeys = {
  Accepted: 'communication.outcomes.Accepted',
  Retried: 'communication.outcomes.Retried',
  Rejected: 'communication.outcomes.Rejected',
  Published: 'communication.outcomes.Published',
  Failed: 'communication.outcomes.Failed',
  Dropped: 'communication.outcomes.Dropped',
} as const satisfies Record<CommunicationOutcome, string>;

/**
 * The gate's delivery states this app knows today. The set is open-ended — Flowis may add one at any
 * time — so resolve through {@link gateStatusLabel}, which falls back to humanizing the raw value
 * rather than rendering a missing key.
 */
const gateStatusLabelKeys = {
  draft: 'gateStatuses.draft',
  new: 'gateStatuses.new',
  ready_to_send: 'gateStatuses.ready_to_send',
  sent: 'gateStatuses.sent',
  accepted: 'gateStatuses.accepted',
  completed: 'gateStatuses.completed',
  rejected: 'gateStatuses.rejected',
  not_valid: 'gateStatuses.not_valid',
  failed_delivery: 'gateStatuses.failed_delivery',
} as const satisfies Record<string, string>;

export function gateStatusLabel(status: string, t: TFunction): string {
  const key =
    status in gateStatusLabelKeys
      ? gateStatusLabelKeys[status as keyof typeof gateStatusLabelKeys]
      : undefined;

  return key !== undefined ? t(key) : humanizeGateStatus(status);
}
